// 일정관리 캘린더: 예약 줄을 상품 담당자별 색으로 구분한다.
// Tailwind 가 찾을 수 있게 클래스명은 전체 문자열로 적는다. 색 단계는 다크모드에서 테마 변수로 자동으로 뒤집힌다.
const MANAGER_PALETTE = [
  { row: 'bg-blue-100 text-blue-800 border-blue-500 hover:bg-blue-200', dot: 'bg-blue-500' },
  { row: 'bg-emerald-100 text-emerald-800 border-emerald-500 hover:bg-emerald-200', dot: 'bg-emerald-500' },
  { row: 'bg-orange-100 text-orange-800 border-orange-500 hover:bg-orange-200', dot: 'bg-orange-500' },
  { row: 'bg-violet-100 text-violet-800 border-violet-500 hover:bg-violet-200', dot: 'bg-violet-500' },
  { row: 'bg-rose-100 text-rose-800 border-rose-500 hover:bg-rose-200', dot: 'bg-rose-500' },
  { row: 'bg-cyan-100 text-cyan-800 border-cyan-500 hover:bg-cyan-200', dot: 'bg-cyan-500' },
  { row: 'bg-amber-100 text-amber-800 border-amber-500 hover:bg-amber-200', dot: 'bg-amber-500' },
  { row: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-500 hover:bg-fuchsia-200', dot: 'bg-fuchsia-500' },
  { row: 'bg-lime-100 text-lime-800 border-lime-500 hover:bg-lime-200', dot: 'bg-lime-500' },
  { row: 'bg-indigo-100 text-indigo-800 border-indigo-500 hover:bg-indigo-200', dot: 'bg-indigo-500' },
  { row: 'bg-teal-100 text-teal-800 border-teal-500 hover:bg-teal-200', dot: 'bg-teal-500' },
  { row: 'bg-pink-100 text-pink-800 border-pink-500 hover:bg-pink-200', dot: 'bg-pink-500' },
];

export const UNASSIGNED_MANAGER_COLOR = {
  row: 'bg-gray-100 text-gray-700 border-gray-400 hover:bg-gray-200',
  dot: 'bg-gray-400',
};

const clean = (value) => (value === null || value === undefined ? '' : String(value).trim());

const isCanceled = (order) => {
  const value = clean(order?.취소여부 ?? order?.취소여부1 ?? order?.취소여부2).toLowerCase();
  return value === '1' || value === 'true';
};

const hashName = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 2147483647;
  }
  return hash;
};

/**
 * 담당자 이름 → 색. 이름으로 자리를 정하므로 달을 넘겨도 대체로 같은 색이지만,
 * 자리가 겹치면 다음 빈 색을 쓰기 때문에 그 달에 함께 나오는 담당자에 따라 색이 바뀔 수 있다.
 * 같은 화면 안에서 구분되는 것을 우선한다. 담당자가 팔레트 수를 넘으면 그때부터 색을 나눠 쓴다.
 */
export const buildManagerColorMap = (names) => {
  const unique = [...new Set((names || []).map(clean).filter(Boolean))].sort();
  const size = MANAGER_PALETTE.length;
  const taken = new Set();
  const colors = new Map();

  unique.forEach((name) => {
    const home = hashName(name) % size;
    let slot = home;
    for (let step = 0; step < size; step += 1) {
      const candidate = (home + step) % size;
      if (!taken.has(candidate)) {
        slot = candidate;
        break;
      }
    }
    taken.add(slot);
    colors.set(name, MANAGER_PALETTE[slot]);
  });

  return colors;
};

/**
 * 행사단체 줄마다 상품 담당자를 찾아 _상품담당 으로 붙인다.
 * 단체 결산 SP 에는 상품담당이 없을 수 있어 주문조회(WSP_S_예약현황_02) 결과에서 주문번호 → 판매번호 → 단체번호 순으로 찾고,
 * 그래도 없으면 줄에 있는 수배담당자를 쓴다.
 */
export const attachProductManagers = (groupRows, orderRows) => {
  const byOrderNo = new Map();
  const bySaleNo = new Map();
  const byGroupNo = new Map();
  const remember = (map, key, manager) => {
    if (key && !map.has(key)) map.set(key, manager);
  };

  (Array.isArray(orderRows) ? orderRows : []).forEach((order) => {
    const manager = clean(order?.상품담당);
    if (!manager) return;
    remember(byOrderNo, clean(order?.주문번호), manager);
    remember(bySaleNo, clean(order?.판매번호 ?? order?.주문판매번호), manager);
    // 주문·판매번호는 그 건 자체라 취소돼도 맞지만, 단체번호는 다른 건을 대표하므로 취소 건과 번호 0(단체 없음)은 뺀다
    const groupNo = clean(order?.행사단체번호);
    if (groupNo !== '0' && !isCanceled(order)) remember(byGroupNo, groupNo, manager);
  });

  return (Array.isArray(groupRows) ? groupRows : []).map((row) => ({
    ...row,
    _상품담당:
      clean(row?.상품담당) ||
      byOrderNo.get(clean(row?.주문번호)) ||
      bySaleNo.get(clean(row?.판매번호 ?? row?.주문판매번호)) ||
      byGroupNo.get(clean(row?.행사단체번호)) ||
      clean(row?.수배담당자) ||
      clean(row?.수배담당) ||
      clean(row?.담당자),
  }));
};
